import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PremiumBadge } from "@/components/PremiumBadge";
import { Users, Trophy, ArrowUp, ArrowDown, Minus } from "lucide-react";

interface Props {
  id: string;
  name: string;
  invite_code: string;
  isAdmin: boolean;
  isPremium?: boolean;
  logo_url?: string | null;
  tournamentName?: string | null;
  playerCount?: number;
  myRank?: number;
  myPoints?: number;
  trend?: number | null;
}

export const LeagueCard = ({ id, name, invite_code, isAdmin, isPremium, logo_url, tournamentName, playerCount, myRank, myPoints, trend }: Props) => {
  const { t } = useTranslation();
  const renderTrend = () => {
    if (trend === null || trend === undefined) return null;
    const base = "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold";
    if (trend > 0) {
      return (
        <span className={`${base} bg-emerald-500/15 text-emerald-600 dark:text-emerald-400`} title={t("league.trendUp")}>
          <ArrowUp className="h-3 w-3" />{trend}
        </span>
      );
    }
    if (trend < 0) {
      return (
        <span className={`${base} bg-destructive/15 text-destructive`} title={t("league.trendDown")}>
          <ArrowDown className="h-3 w-3" />{Math.abs(trend)}
        </span>
      );
    }
    return (
      <span className={`${base} bg-muted text-muted-foreground`} title={t("league.trendSame")}>
        <Minus className="h-3 w-3" />
      </span>
    );
  };
  return (
    <Link to={`/league/${id}`} className="h-full block">
      <Card className="h-full transition active:scale-[0.99] hover:shadow-elevated flex flex-col">
        <CardHeader className="pb-4">
          <CardTitle className="flex flex-wrap items-center justify-between gap-y-2 gap-x-2">
            <span className="flex items-center gap-2 min-w-0">
              {logo_url ? (
                <img src={logo_url} alt="" className="h-8 w-8 rounded-lg object-cover ring-1 ring-border shrink-0" />
              ) : (
                <span className="h-8 w-8 rounded-lg bg-gradient-hero text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">
                  {name.charAt(0).toUpperCase()}
                </span>
              )}
              <span className="truncate">{name}</span>
            </span>
            <span className="flex items-center gap-1.5 shrink-0">
              {isPremium && (
                <PremiumBadge label={t("league.premium")} />
              )}
              {isAdmin && (
                <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-accent text-accent-foreground">
                  {t("common.admin")}
                </span>
              )}
            </span>
          </CardTitle>
          {tournamentName && (
            <p className="text-xs font-medium text-primary/90 truncate">{tournamentName}</p>
          )}
          <CardDescription className="font-mono tracking-widest text-xs">{invite_code}</CardDescription>
          {(typeof playerCount === "number" || typeof myRank === "number") && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
              {typeof playerCount === "number" && (
                <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {playerCount} {t("league.players")}
                </span>
              )}
              {typeof myRank === "number" && (
                <span className="flex items-center gap-1">
                  <Trophy className="h-3.5 w-3.5" />
                  {t("league.rank")}{myRank}
                  {renderTrend()}
                </span>
              )}
              {typeof myPoints === "number" && (
                <span className="flex items-center gap-1">
                  <span className="font-semibold text-foreground">{myPoints}</span>
                  <span className="text-muted-foreground">{t("league.points")}</span>
                </span>
              )}
            </div>
          )}
        </CardHeader>
      </Card>
    </Link>
  );
};
